// My actions' Open agent feedback tab: every agent question waiting on an operator across
// the sites the daily run works, in the Agent feedback section's rows, answered in place.
// Opens on the viewer's questions and nobody's, as the badge counts; the person filter shows
// anyone's. See docs/Y2026-Q4/design-open-agent-feedback.md and design-feedback-assignment.md.
import { useMemo, useState } from "react";

import {
  type FeedbackQuestion,
  mineOrUnassigned,
  needsOperator,
  tabsShowing,
  teamName,
  useOpenFeedback,
} from "../api/hooks/useAgentFeedback";
import { useRunsDescribe } from "../api/hooks/useRunsDescribe";
import { useHashOpen } from "../utils/useScrollToHash";
import { QuestionTable } from "./AgentFeedbackSection";
import { PersonMark } from "./AssigneePicker";
import { PersonSwitcher } from "./PersonSwitcher";
import { Chevron } from "./Chevron";
import { MultiSelectFilter } from "./MultiSelectFilter";
import own from "./AgentFeedbackSection.module.css";
import styles from "./UnreadAnswers.module.css";

const SITE_NOUN = { one: "site", many: "sites" };
const SPECIALIST_NOUN = { one: "specialist", many: "specialists" };

type Order = "oldest" | "site";

const sitesOf = (q: FeedbackQuestion): string[] =>
  (q.sites ?? []).map((s) => s.project_id);
const siteName = (q: FeedbackQuestion, id: string): string =>
  q.project_names[id] ?? id;
const firstSite = (q: FeedbackQuestion): string =>
  siteName(q, sitesOf(q)[0] ?? "");

// The person filter's keys beside the team members' emails.
const MINE = "mine";
const EVERYONE = "everyone";
const UNASSIGNED = "unassigned";

export const OpenAgentFeedback = ({ viewer }: { viewer: string }) => {
  const query = useOpenFeedback(true, viewer);
  // Opens on what the badge counts: the viewer's questions and nobody's.
  const [person, setPerson] = useState<string>(MINE);
  const [sites, setSites] = useState<string[]>([]);
  const [specialists, setSpecialists] = useState<string[]>([]);
  const [order, setOrder] = useState<Order>("oldest");
  const [appliedOpen, setAppliedOpen] = useState(false);
  const { openIds, toggle } = useHashOpen();
  const all = useMemo(() => query.data?.questions ?? [], [query.data]);
  // Only an opened row names its conversations, and the whole list can name more runs
  // than one describe call takes.
  const { byId: runs } = useRunsDescribe(
    all
      .filter((q) => openIds.has(q.question_id))
      .flatMap((q) => [
        q.raised_by_run_id,
        ...(q.review_conversation_id ? [q.review_conversation_id] : []),
      ]),
  );
  // Filters name sites and specialists as the rows do, so a filter reads like its column.
  const siteOptions = useMemo(
    () =>
      [...new Set(all.flatMap((q) => sitesOf(q).map((p) => siteName(q, p))))].sort(),
    [all],
  );
  const specialistOptions = useMemo(
    () => [...new Set(all.flatMap(tabsShowing))].sort(),
    [all],
  );

  if (query.isLoading)
    return <p className={styles.muted}>Loading the agents&apos; questions…</p>;
  if (query.isError || !query.data)
    return (
      <p className={styles.error}>
        Could not load the agents&apos; questions. Reload to try again.
      </p>
    );

  const team = query.data.team ?? {};
  const holds = (key: string, q: FeedbackQuestion): boolean =>
    key === EVERYONE
      ? true
      : key === MINE
        ? mineOrUnassigned(q, viewer)
        : key === UNASSIGNED
          ? !q.assignee
          : q.assignee?.toLowerCase() === key.toLowerCase();
  const matches = (q: FeedbackQuestion) =>
    holds(person, q) &&
    (!sites.length || sitesOf(q).some((p) => sites.includes(siteName(q, p)))) &&
    (!specialists.length || tabsShowing(q).some((a) => specialists.includes(a)));
  const byAsked = (a: FeedbackQuestion, b: FeedbackQuestion) =>
    a.created_at.localeCompare(b.created_at);
  const waiting = all
    .filter((q) => needsOperator(q) && matches(q))
    .sort((a, b) =>
      order === "site"
        ? firstSite(a).localeCompare(firstSite(b)) || byAsked(a, b)
        : byAsked(a, b),
    );
  // Answered and being applied. A skip or an answer applied at once closes the question;
  // it leaves on the next refetch rather than sitting here meanwhile.
  const applied = all
    .filter((q) => q.status === "answered" && matches(q))
    .sort(byAsked);
  const open = all.filter(needsOperator);
  const total = open.length;
  const filtered = sites.length > 0 || specialists.length > 0;
  const table = {
    columns: "open" as const,
    showProjects: false,
    openIds,
    toggle,
    runs,
    team,
  };
  const people = Object.entries(team).sort(([a, an], [b, bn]) =>
    a.toLowerCase() === viewer ? -1 : b.toLowerCase() === viewer ? 1 : an.localeCompare(bn),
  );
  const titles: Record<string, string> = {
    [MINE]: "Mine and unassigned",
    [EVERYONE]: "Everyone's questions",
    [UNASSIGNED]: "Unassigned questions",
  };
  const titleOf = (key: string): string =>
    titles[key] ??
    (key.toLowerCase() === viewer ? "My questions" : `${teamName(team, key)}'s questions`);
  const markOf = (key: string) =>
    key === EVERYONE ? (
      <PersonMark name="" tone="everyone" />
    ) : key === UNASSIGNED ? (
      <PersonMark name="" tone="nobody" />
    ) : key === MINE ? (
      <PersonMark name={teamName(team, viewer)} tone="person" />
    ) : (
      <PersonMark name={teamName(team, key)} tone="person" />
    );
  const option = (key: string, label: string) => ({
    key,
    label: (
      <span className={own.personOption}>
        {markOf(key)}
        {label}
      </span>
    ),
    count: `${open.filter((q) => holds(key, q)).length} open`,
  });
  return (
    <div className={styles.panel}>
      <p className={styles.muted}>
        Every question an agent is waiting on us to answer, across the projects
        the daily run works. Answer here or on the site; it is one answer either
        way.
      </p>
      {all.length > 0 && (
        <div className={styles.filters} role="group" aria-label="Show">
          <PersonSwitcher
            title={
              <span className={own.personOption}>
                {markOf(person)}
                {titleOf(person)}
              </span>
            }
            current={person}
            onChoose={setPerson}
            sections={[
              [
                option(MINE, "Mine and unassigned"),
                option(EVERYONE, "Everyone"),
                option(UNASSIGNED, "Unassigned"),
              ],
              people.map(([email, name]) =>
                option(email, `${name}${email.toLowerCase() === viewer ? " (you)" : ""}`),
              ),
            ]}
            wrap={(button) => <span className={own.personFilter}>{button}</span>}
          />
          <MultiSelectFilter
            noun={SITE_NOUN}
            size="compact"
            options={siteOptions}
            selected={sites}
            onChange={setSites}
          />
          <MultiSelectFilter
            noun={SPECIALIST_NOUN}
            size="compact"
            searchable={false}
            options={specialistOptions}
            selected={specialists}
            onChange={setSpecialists}
          />
          {filtered && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                setSites([]);
                setSpecialists([]);
              }}
            >
              Clear filters
            </button>
          )}
          <span className={styles.count}>
            Showing {waiting.length} of {total} open
          </span>
          <label className={styles.control}>
            Sort
            <select
              value={order}
              onChange={(e) => setOrder(e.target.value as Order)}
            >
              <option value="oldest">Oldest first</option>
              <option value="site">Site</option>
            </select>
          </label>
        </div>
      )}
      <div className={styles.card}>
        {waiting.length === 0 ? (
          <p className={styles.caughtUp}>
            {total === 0
              ? "No agent is waiting on an answer."
              : person === MINE && !filtered
                ? "Nothing is assigned to you, and nothing is unassigned. Choose Everyone to see the rest."
                : "No open feedback for this person, site and specialist."}
          </p>
        ) : (
          <QuestionTable questions={waiting} head {...table} />
        )}
        {applied.length > 0 && (
          <div className={own.addressed}>
            <button
              type="button"
              className={own.addressedToggle}
              aria-expanded={appliedOpen}
              onClick={() => setAppliedOpen(!appliedOpen)}
            >
              <span aria-hidden="true">
                <Chevron tree open={appliedOpen} />
              </span>
              Being applied ({applied.length})
            </button>
            {appliedOpen && (
              <QuestionTable questions={applied} head={false} {...table} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
